// ─────────────────────────────────────────────────────────────────────────────
// CLOTH 2D — canvas-based cloth grid renderer for the low-end device path
// Extracted from try-on.js cloth engine and made into a proper class.
// Draws textured quads from a deformed 2D particle mesh onto a canvas overlay.
// ─────────────────────────────────────────────────────────────────────────────

import type { GarmentSchema } from "./garment-schema";
import type { RetargetPose } from "./retarget";

interface Particle {
  x: number;
  y: number;
  u: number;
  v: number;
  pinned: boolean;
}

interface Cloth2DOptions {
  cols: number;
  rows: number;
  stiffness: number;
  drape: number;
  wind: number;
}

function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function easeInOut(t: number) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }
function easeOut(t: number) { return 1 - (1 - t) * (1 - t); }

export class Cloth2D {
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private velocities: Array<{ vx: number; vy: number }> = [];
  private windPhase = 0;

  constructor(
    private canvas: HTMLCanvasElement,
    private opts: Cloth2DOptions
  ) {
    this.ctx = canvas.getContext("2d")!;
  }

  // ── Build or rebuild the cloth grid from current pose ─────────────────────
  private buildMesh(schema: GarmentSchema, pose: RetargetPose) {
    this.particles = [];
    this.velocities = [];

    const { cols, rows } = this.opts;
    for (let r = 0; r <= rows; r++) {
      for (let c = 0; c <= cols; c++) {
        const u = c / cols;
        const v = r / rows;
        const w = this._widthAtProgress(pose, v, schema);
        const x = pose.hipCenter.x + (u - 0.5) * w;
        const y = pose.hipCenter.y - pose.torsoUp.y * 0.14 + v * this._torsoH(pose);
        this.particles.push({ x, y, u, v, pinned: r === 0 });
        this.velocities.push({ vx: 0, vy: 0 });
      }
    }
  }

  private _torsoH(pose: RetargetPose): number {
    const sh = pose.leftShoulder.y + pose.rightShoulder.y;
    const hip = pose.hipCenter.y;
    return Math.abs(sh / 2 - hip);
  }

  // ── Simulate one frame of cloth physics ───────────────────────────────────
  private simulate(schema: GarmentSchema, pose: RetargetPose, dt: number) {
    const { stiffness, drape } = this.opts;
    const gravity = 9.8 * drape * dt;
    this.windPhase += dt * 0.8;

    const windX = Math.sin(this.windPhase * 2.3) * 0.4 * drape * (schema.physics.wind ?? 1);
    const windY = Math.sin(this.windPhase * 1.7) * 0.2 * drape;

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      if (p.pinned) continue;

      const { u, v } = p;
      const targetW  = this._widthAtProgress(pose, v, schema);
      const targetX  = pose.hipCenter.x + (u - 0.5) * targetW;
      const targetY  = pose.hipCenter.y - this._torsoH(pose) * 0.14 + v * this._torsoH(pose);

      const springK = stiffness * 22;
      const dx = targetX - p.x;
      const dy = targetY - p.y;

      const vel = this.velocities[i];
      vel.vx += (dx * springK + windX * 6) * dt;
      vel.vy += (dy * springK + gravity * 60 + windY * 4) * dt;

      const damping = 0.72 + stiffness * 0.16;
      vel.vx *= damping;
      vel.vy *= damping;

      const wNoise  = (Math.sin(i * 7.3 + this.windPhase * 3.1) * 0.5 + 0.5) * drape;
      const wrinkleX = Math.sin(v * Math.PI * 4 + this.windPhase) * wNoise * 1.8;
      const wrinkleY = Math.cos(u * Math.PI * 3 + this.windPhase * 0.7) * wNoise * 0.8;

      p.x = targetX + vel.vx * 0.1 + wrinkleX;
      p.y = targetY + vel.vy * 0.1 + wrinkleY;
    }
  }

  // ── Draw textured cloth mesh onto canvas ──────────────────────────────────
  draw(
    img: HTMLImageElement,
    pose: RetargetPose,
    schema: GarmentSchema,
    isMirrored: boolean
  ) {
    const { cols, rows } = this.opts;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const ctx = this.ctx;

    ctx.clearRect(0, 0, w, h);

    if (this.particles.length === 0) {
      this.buildMesh(schema, pose);
    }

    this.simulate(schema, pose, 1 / 60);

    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;

    ctx.save();
    ctx.globalAlpha = 0.96;

    if (isMirrored) {
      const cx = this.particles.reduce((s, p) => s + p.x, 0) / this.particles.length;
      ctx.translate(cx * 2, 0);
      ctx.scale(-1, 1);
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i00 = r * (cols + 1) + c;
        const i10 = i00 + 1;
        const i01 = i00 + (cols + 1);
        const i11 = i01 + 1;

        const p00 = this.particles[i00];
        const p10 = this.particles[i10];
        const p01 = this.particles[i01];
        const p11 = this.particles[i11];
        if (!p00 || !p10 || !p01 || !p11) continue;

        this._drawTri(ctx, img,
          p00.x, p00.y, p00.u, p00.v,
          p10.x, p10.y, p10.u, p10.v,
          p01.x, p01.y, p01.u, p01.v,
          imgW, imgH
        );
        this._drawTri(ctx, img,
          p10.x, p10.y, p10.u, p10.v,
          p11.x, p11.y, p11.u, p11.v,
          p01.x, p01.y, p01.u, p01.v,
          imgW, imgH
        );
      }
    }

    ctx.restore();
  }

  // ── Textured triangle via canvas affine transform ─────────────────────────
  private _drawTri(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x0: number, y0: number, u0: number, v0: number,
    x1: number, y1: number, u1: number, v1: number,
    x2: number, y2: number, u2: number, v2: number,
    imgW: number, imgH: number
  ) {
    const denom = (u1 - u0) * (v2 - v0) - (u2 - u0) * (v1 - v0);
    if (Math.abs(denom) < 1e-8) return;

    const a = ((x1 - x0) * (v2 - v0) - (x2 - x0) * (v1 - v0)) / denom;
    const b = ((x2 - x0) * (u1 - u0) - (x1 - x0) * (u2 - u0)) / denom;
    const c = x0 - a * u0 - b * v0;
    const d = ((y1 - y0) * (v2 - v0) - (y2 - y0) * (v1 - v0)) / denom;
    const e = ((y2 - y0) * (u1 - u0) - (y1 - y0) * (u2 - u0)) / denom;
    const f = y0 - d * u0 - e * v0;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.closePath();
    ctx.clip();
    ctx.transform(a * imgW, d * imgH, b * imgW, e * imgH, c, f);
    ctx.drawImage(img, 0, 0, 1, 1);
    ctx.restore();
  }

  private _widthAtProgress(pose: RetargetPose, progress: number, schema: GarmentSchema): number {
    const stiffness = schema.physics.stiffness;
    const taperFactor = 1 - stiffness * 0.3;
    const shoulderW = pose.leftShoulder.distanceTo(pose.rightShoulder) * schema.widthScale;
    const waistW    = shoulderW * 0.82;
    const hipW      = shoulderW * 1.04;

    if (progress < 0.32) {
      return lerp(shoulderW * 1.04, shoulderW * 0.96, progress / 0.32);
    }
    if (progress < 0.52) {
      const t = (progress - 0.32) / 0.20;
      return lerp(shoulderW * 0.96, waistW, easeInOut(t) * taperFactor + (1 - taperFactor));
    }
    return lerp(waistW, hipW, easeOut((progress - 0.52) / 0.48));
  }

  reset() {
    this.particles = [];
    this.velocities = [];
    this.windPhase = 0;
  }
}
